import { useState } from "react";
import { phCapture } from "@/lib/posthog";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FileText, Home, PlusCircle, Megaphone, CreditCard, LogOut, User, Menu, Crown, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/contexts/AuthContext";
import { useUserPlan } from "@/hooks/useUserPlan";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import SentryTestButton from "@/components/SentryTestButton";
import CookieConsent from "@/components/CookieConsent";

const Layout = ({ children }: { children: React.ReactNode }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, loading, signOut } = useAuth();
  const { plan, isAdmin } = useUserPlan();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [cookieSettingsOpen, setCookieSettingsOpen] = useState(false);

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const { error } = await supabase.functions.invoke("delete-account", {
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (error) throw error;
      toast.success("Account deleted.");
      await signOut();
      navigate("/");
    } catch (err: any) {
      toast.error("Failed to delete account: " + (err.message || "Unknown error"));
    } finally {
      setDeleting(false);
      setDeleteAccountOpen(false);
    }
  };

  const navItems = [
    { to: "/", label: "Home", icon: Home },
    { to: "/generate", label: "Generate", icon: PlusCircle },
    ...(user ? [{ to: "/history", label: "Reports", icon: FileText }] : []),
    { to: "/landing", label: "Landing", icon: Megaphone },
    { to: "/pricing", label: "Pricing", icon: CreditCard },
  ];

  return (
    <div className="min-h-screen flex flex-col dark-page overflow-x-hidden">
      <header className="sticky top-0 z-50 border-b border-[hsl(var(--dark-border))] bg-[hsl(var(--dark-base)/.9)] backdrop-blur-md">
        <div className="container mx-auto flex h-14 items-center justify-between px-4">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <span className="text-lg font-bold tracking-tight gradient-text" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              ScopSift
            </span>
            {isAdmin && (
              <Badge variant="outline" className="ml-2 text-[10px] px-1.5 py-0 border-amber-500/50 text-amber-400 font-normal">
                Admin mode
              </Badge>
            )}
          </Link>

          <div className="flex items-center gap-2">
            {/* Desktop nav */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map(({ to, label, icon: Icon }) => (
                <Link
                  key={to}
                  to={to}
                  className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                    pathname === to
                      ? "bg-gradient-to-r from-blue-500 to-teal-400 text-white"
                      : "text-slate-300 hover:text-white hover:bg-white/5"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{label}</span>
                </Link>
              ))}
            </nav>

            {/* Mobile hamburger */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden text-slate-300 hover:text-white">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="bg-[hsl(var(--dark-base))] border-[hsl(var(--dark-border))] w-64">
                <nav className="flex flex-col gap-1 mt-6">
                  {navItems.map(({ to, label, icon: Icon }) => (
                    <Link
                      key={to}
                      to={to}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-2.5 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                        pathname === to
                          ? "bg-gradient-to-r from-blue-500 to-teal-400 text-white"
                          : "text-slate-300 hover:text-white hover:bg-white/5"
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      {label}
                    </Link>
                  ))}
                </nav>
              </SheetContent>
            </Sheet>

            {!loading && (
              user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="ml-2 text-slate-300 hover:text-white">
                      <User className="h-4 w-4 mr-1.5" />
                      <span className="hidden sm:inline">Account</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="dark-surface border">
                    <DropdownMenuLabel className="text-xs font-normal dark-muted truncate max-w-[200px]">
                      {user.email}
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-[hsl(var(--dark-border))]" />
                    {plan === "free" && (
                      <DropdownMenuItem asChild onClick={() => phCapture("upgrade_clicked", { source: "nav" })}>
                        <Link to="/pricing"><Crown className="h-4 w-4 mr-2 text-amber-400" /> Upgrade to Pro</Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={signOut}>
                      <LogOut className="h-4 w-4 mr-2" /> Sign out
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-[hsl(var(--dark-border))]" />
                    <DropdownMenuItem onClick={() => setDeleteAccountOpen(true)} className="text-red-400 focus:text-red-400">
                      <Trash2 className="h-4 w-4 mr-2" /> Delete account
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <div className="ml-2 flex items-center gap-1.5">
                  <Button variant="ghost" size="sm" asChild className="text-slate-300 hover:text-white">
                    <Link to="/signin">Sign in</Link>
                  </Button>
                  <Button variant="outline" size="sm" asChild className="border-[hsl(var(--dark-border))] text-slate-300 hover:text-white hidden sm:inline-flex">
                    <Link to="/signin?mode=signup">Create account</Link>
                  </Button>
                </div>
              )
            )}
          </div>
        </div>
      </header>
      <main className="flex-1 page-glow">{children}</main>
      <footer className="border-t border-[hsl(var(--dark-border))] py-4 text-center text-xs dark-muted space-y-1">
        <div className="flex items-center justify-center gap-2">
          <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
          <span>·</span>
          <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
          <span>·</span>
          <Link to="/disclaimer" className="hover:text-white transition-colors">Disclaimer</Link>
          <span>·</span>
          <Link to="/support" className="hover:text-white transition-colors">Support</Link>
          <span>·</span>
          <button onClick={() => setCookieSettingsOpen(true)} className="hover:text-white transition-colors">Cookie settings</button>
        </div>
        <p>© 2026 ScopSift · Amazon UK Fitness Accessories</p>
        {import.meta.env.DEV && <SentryTestButton />}
      </footer>

      <AlertDialog open={deleteAccountOpen} onOpenChange={setDeleteAccountOpen}>
        <AlertDialogContent className="bg-[#0f1629] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete your account?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently delete all your reports, data, and account. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/10 text-slate-300">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700 text-white" onClick={handleDeleteAccount} disabled={deleting}>
              {deleting ? "Deleting…" : "Delete my account"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CookieConsent forceOpen={cookieSettingsOpen} onClose={() => setCookieSettingsOpen(false)} />
    </div>
  );
};

export default Layout;
